import { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import FileUploadZone from '../common/FileUploadZone';
import ListLoadError from './ListLoadError';
import api from '../../services/api';
import {
  getProductOfferByProductId,
  createProductOffer,
  updateProductOffer,
  deleteProductOffer
} from '../../services/productOfferService';
import styles from './ProductOfferEditorModal.module.css';

// === Supply pricing summary (card 4071) ==============================================
// `product_dosages.supply_pricing` is TIER-KEYED — { "1": { access, one, plus }, ... } —
// so reading it as a flat { month: cents } map made `cents / 100` NaN on every row.
// Tier order and labels mirror ProductsPage's STARTING_AT_TIERS.
const SUPPLY_TIERS = [
  { key: 'access', label: 'Access' },
  { key: 'one', label: 'ONE' },
  { key: 'plus', label: 'PLUS' }
];

// Accepts BOTH shapes, exactly like pricingEngine's extractMedicationPrice: a tier-keyed
// object, or a bare number left over from the flat era. A flat rate is what every tier is
// charged, so it is shown in all three columns.
// ⛔ Deliberately NO `?? raw.access` fallback: pricingEngine falls back because it must
// produce a charge, but a per-tier *display* that silently borrows another tier's number
// is the same class of defect as the NaN. An unpriced tier reads as an em dash.
const formatTierRate = (raw, tierKey) => {
  const cents = raw !== null && typeof raw === 'object' ? raw[tierKey] : raw;
  return typeof cents === 'number' ? `$${(cents / 100).toFixed(0)}` : '—';
};

// Only numeric keys are durations — the sibling `supply_pricing_meta` carries bookkeeping
// keys (migratedAt, originalFlatPricing), so this reader does not assume its own column
// is exempt. Returns '' when there is nothing quotable, which renders "No supply pricing".
const formatSupplyPricing = (supplyPricing) =>
  Object.entries(supplyPricing ?? {})
    .filter(([months]) => /^\d+$/.test(months))
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([months, raw]) =>
      `${months}mo: ${SUPPLY_TIERS.map(({ key }) => formatTierRate(raw, key)).join(' / ')}`
    )
    .join('  |  ');

/**
 * ProductOfferEditorModal - Modal for creating/editing individual product offers
 * Props:
 *   isOpen - Boolean to control modal visibility
 *   onClose - Callback when modal is closed
 *   product - The product object to create/edit offer for
 *   onSave - Callback after successful save
 */
const ProductOfferEditorModal = ({
  isOpen,
  onClose,
  product,
  onSave
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offerData, setOfferData] = useState(null);
  const [existingOfferId, setExistingOfferId] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  const isCreatingNew = !existingOfferId;

  // Load existing offer data when modal opens
  useEffect(() => {
    if (!isOpen || !product) return;

    const loadData = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        // Check if product already has an offer
        const existingOffer = await getProductOfferByProductId(product.id);
        
        if (existingOffer) {
          setExistingOfferId(existingOffer.id);
          setOfferData({
            title: existingOffer.title || '',
            description: existingOffer.description || '',
            heroImage: existingOffer.heroImage || null,
            galleryImages: existingOffer.galleryImages || [],
            pricingDisplay: existingOffer.pricingDisplay || '',
            ctaText: existingOffer.ctaText || 'Get Started',
            badgeText: existingOffer.badgeText || '',
            features: existingOffer.features || [],
            categoryLabel: existingOffer.categoryLabel || '',
            fdaStatus: existingOffer.fdaStatus || '',
            priceUnit: existingOffer.priceUnit || ''
          });
        } else {
          // Creating new offer
          setExistingOfferId(null);
          setOfferData({
            title: product.name || '',
            description: product.description || '',
            heroImage: null,
            galleryImages: [],
            pricingDisplay: '',
            ctaText: 'Get Started',
            badgeText: '',
            categoryLabel: '',
            fdaStatus: '',
            priceUnit: '',
            features: []
          });
        }
      } catch (error) {
        // This modal stays MOUNTED for the life of the page, so without these two
        // resets a failed open leaves the PREVIOUS product's offer in the form under
        // the new product's banner, with `existingOfferId` still pointing at the old
        // record - i.e. Save would write the wrong row. Every load here is
        // user-initiated (the operator opened the modal), so there is no background
        // blip to tolerate and stale content is a second lie, not a kindness.
        setOfferData(null);
        setExistingOfferId(null);
        setLoadError(true);
        toast.error('Failed to load offer data');
        console.error('Failed to load offer data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product, reloadKey]);

  // Validation
  const validateForm = () => {
    if (!offerData?.title?.trim()) {
      toast.error('Title is required');
      return false;
    }
    const hasProductImage = Boolean(product?.lifestyleImageUrl || product?.images?.length);
    if (!offerData?.heroImage && !offerData?.galleryImages?.length && !hasProductImage) {
      toast.error('At least one image is required (hero or gallery)');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        productId: product.id,
        title: offerData.title,
        description: offerData.description,
        heroImage: offerData.heroImage,
        galleryImages: offerData.galleryImages,
        pricingDisplay: offerData.pricingDisplay,
        ctaText: offerData.ctaText,
        badgeText: offerData.badgeText,
        features: offerData.features,
        categoryLabel: offerData.categoryLabel,
        fdaStatus: offerData.fdaStatus,
        priceUnit: offerData.priceUnit
      };

      if (isCreatingNew) {
        await createProductOffer(payload);
        toast.success('Offer created successfully');
      } else {
        await updateProductOffer(existingOfferId, payload);
        toast.success('Offer updated successfully');
      }

      onSave?.();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save offer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingOfferId) return;
    if (!window.confirm('Are you sure you want to delete this offer?')) return;

    try {
      await deleteProductOffer(existingOfferId);
      toast.success('Offer deleted successfully');
      onSave?.();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to delete offer');
    }
  };

  // Image upload handlers
  const handleHeroUpload = async (file) => {
    if (!file) {
      setOfferData(prev => ({ ...prev, heroImage: null }));
      return;
    }

    setUploadingHero(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await api.upload('/admin/uploads/image', formData);
      
      setOfferData(prev => ({ ...prev, heroImage: response.url }));
      toast.success('Hero image uploaded');
    } catch (error) {
      toast.error('Failed to upload hero image');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleGalleryUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingGallery(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        const response = await api.upload('/admin/uploads/image', formData);
        return response.url;
      });

      const urls = await Promise.all(uploadPromises);
      
      setOfferData(prev => ({
        ...prev,
        galleryImages: [...(prev.galleryImages || []), ...urls]
      }));
      toast.success(urls.length + ' image(s) uploaded');
    } catch (error) {
      toast.error('Failed to upload gallery images');
    } finally {
      setUploadingGallery(false);
    }
  };

  const removeGalleryImage = (index) => {
    setOfferData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index)
    }));
  };

  // Feature management
  const addFeature = () => {
    setOfferData(prev => ({
      ...prev,
      features: [...(prev.features || []), '']
    }));
  };

  const updateFeature = (index, value) => {
    const features = [...(offerData.features || [])];
    features[index] = value;
    setOfferData(prev => ({ ...prev, features }));
  };

  const removeFeature = (index) => {
    const features = [...(offerData.features || [])];
    features.splice(index, 1);
    setOfferData(prev => ({ ...prev, features }));
  };

  // A failed load tells us NOTHING about whether an offer exists, so the title must
  // not assert 'Create' - that is exactly the affordance which is only safe when the
  // absence is real.
  const modalTitle = loadError
    ? 'Product Offer'
    : isCreatingNew ? 'Create Product Offer' : 'Edit Product Offer';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="large">
      <div className={styles.editor}>
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading...</p>
          </div>
        ) : loadError ? (
          <ListLoadError
            message="Could not load this offer"
            onRetry={() => setReloadKey((key) => key + 1)}
          />
        ) : offerData ? (
          <>
            {/* Product Info Banner */}
            <div className={styles.productBanner}>
              <span className={styles.productLabel}>Product:</span>
              <span className={styles.productName}>{product?.name}</span>
            </div>

            {/* Supply Pricing Summary */}
            {product?.dosages && product.dosages.length > 0 && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Supply Pricing (from Dosages)</h3>
                <div style={{ background: '#faf5ef', borderRadius: '8px', padding: '0.75rem', fontSize: '0.8rem' }}>
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.7rem', color: '#999' }}>
                    Rate per month by tier: {SUPPLY_TIERS.map(({ label }) => label).join(' / ')}
                  </div>
                  {product.dosages.filter(d => d.isActive !== false).map(d => {
                    const summary = formatSupplyPricing(d.supplyPricing);
                    return (
                      <div key={d.id} style={{ marginBottom: '0.5rem' }}>
                        <strong>{d.amount}</strong>
                        {summary ? (
                          <span style={{ marginLeft: '0.5rem', color: '#737373' }}>{summary}</span>
                        ) : (
                          <span style={{ marginLeft: '0.5rem', color: '#999' }}>No supply pricing</span>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#999' }}>
                    Supply pricing is managed in the Product Editor &rarr; Dosage section
                  </div>
                </div>
              </div>
            )}

            {/* Basic Info Section */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Offer Details</h3>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Title *</label>
                  <input
                    type="text"
                    value={offerData.title || ''}
                    onChange={(e) => setOfferData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Exclusive Semaglutide Offer"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Badge Text</label>
                  <input
                    type="text"
                    value={offerData.badgeText || ''}
                    onChange={(e) => setOfferData(prev => ({ ...prev, badgeText: e.target.value }))}
                    placeholder="e.g., Best Seller, Limited Time"
                  />
                </div>
              </div>
              <div className={styles.formGroupFull}>
                <label>Description</label>
                <textarea
                  value={offerData.description || ''}
                  onChange={(e) => setOfferData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this offer..."
                  rows={4}
                />
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Category Label</label>
                  <input
                    type="text"
                    value={offerData.categoryLabel || ''}
                    onChange={(e) => setOfferData(prev => ({ ...prev, categoryLabel: e.target.value }))}
                    placeholder="e.g., a proven GLP-1 injectable"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>FDA Status</label>
                  <input
                    type="text"
                    value={offerData.fdaStatus || ''}
                    onChange={(e) => setOfferData(prev => ({ ...prev, fdaStatus: e.target.value }))}
                    placeholder="e.g., Compounded medication, not FDA-approved"
                  />
                </div>
              </div>
            </div>

            {/* Images Section */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Images</h3>
              <p className={styles.sectionHint}>
                At least one image is required (hero or gallery)
              </p>
              
              {/* Hero Image */}
              <div className={styles.imageSection}>
                <label className={styles.imageLabel}>Hero Image</label>
                {offerData.heroImage ? (
                  <div className={styles.heroPreview}>
                    <img loading="lazy" src={offerData.heroImage} alt="Hero" />
                    <button
                      type="button"
                      className={styles.removeImageBtn}
                      onClick={() => setOfferData(prev => ({ ...prev, heroImage: null }))}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <FileUploadZone
                    accept="image/*"
                    maxSize={5242880}
                    onFileSelect={handleHeroUpload}
                    disabled={uploadingHero}
                    placeholder={uploadingHero ? 'Uploading...' : 'Click to upload hero image'}
                    hint="Recommended: 1200x800px, max 5MB"
                  />
                )}
              </div>

              {/* Gallery Images */}
              <div className={styles.imageSection}>
                <label className={styles.imageLabel}>Gallery Images</label>
                {offerData.galleryImages?.length > 0 && (
                  <div className={styles.galleryGrid}>
                    {offerData.galleryImages.map((url, index) => (
                      <div key={index} className={styles.galleryItem}>
                        <img loading="lazy" src={url} alt={'Gallery ' + (index + 1)} />
                        <button
                          type="button"
                          className={styles.removeImageBtn}
                          onClick={() => removeGalleryImage(index)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <FileUploadZone
                  accept="image/*"
                  maxSize={5242880}
                  multiple
                  onFileSelect={handleGalleryUpload}
                  disabled={uploadingGallery}
                  placeholder={uploadingGallery ? 'Uploading...' : 'Click to add gallery images'}
                  hint="Add multiple images for the gallery"
                  className={styles.galleryUploader}
                />
              </div>
            </div>

            {/* Pricing & CTA Section */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Pricing & Call to Action</h3>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Pricing Display</label>
                  <input
                    type="text"
                    value={offerData.pricingDisplay || ''}
                    onChange={(e) => setOfferData(prev => ({ ...prev, pricingDisplay: e.target.value }))}
                    placeholder="e.g., Starting at $199/month"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>CTA Button Text</label>
                  <input
                    type="text"
                    value={offerData.ctaText || ''}
                    onChange={(e) => setOfferData(prev => ({ ...prev, ctaText: e.target.value }))}
                    placeholder="e.g., Get Started"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Price Unit</label>
                  <input
                    type="text"
                    value={offerData.priceUnit || ''}
                    onChange={(e) => setOfferData(prev => ({ ...prev, priceUnit: e.target.value }))}
                    placeholder="e.g., /month"
                  />
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Features</h3>
                <button className={styles.addButton} onClick={addFeature} type="button">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Feature
                </button>
              </div>
              <p className={styles.sectionHint}>
                List key features or benefits of this offer
              </p>
              <div className={styles.featuresList}>
                {(offerData.features || []).map((feature, index) => (
                  <div key={index} className={styles.featureItem}>
                    <input
                      type="text"
                      value={feature || ''}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder="e.g., Free shipping included"
                      className={styles.featureInput}
                    />
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => removeFeature(index)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
                {(offerData.features || []).length === 0 && (
                  <p className={styles.emptyHint}>No features added yet. Click Add Feature to add bullet points.</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className={styles.actions}>
              {!isCreatingNew && (
                <button type="button" className={styles.deleteButton} onClick={handleDelete}>
                  Delete Offer
                </button>
              )}
              <div className={styles.actionsSpacer} />
              <button type="button" className={styles.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : isCreatingNew ? 'Create Offer' : 'Save Changes'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default ProductOfferEditorModal;
